import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Tracks recently visited records so the sidebar can offer a "Recent" list.
 *
 * A resilience assessment is rarely a single page visit — you open an IBS, look
 * at its components, go back to it, then compare it against another. The list
 * is what removes that navigation from memory.
 *
 * Deliberately client-side and per-browser: this is a navigation convenience,
 * not audit data, and it must never become a second record of who looked at
 * what. Nothing here is sent anywhere.
 */

const STORAGE_KEY = 'or-recent-routes'
const MAX_ENTRIES = 5

export interface RecentRoute {
  /** Route path, used as the identity and the link target. */
  path: string
  /** The record's own name, e.g. "CHAPS Payment Processing". */
  label: string
  /** The section it was reached through, e.g. "IBS Dashboard". */
  context: string
  /** Epoch ms, so the list can be ordered without re-sorting on read. */
  visitedAt: number
}

function read(): RecentRoute[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (entry): entry is RecentRoute =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as RecentRoute).path === 'string' &&
        typeof (entry as RecentRoute).label === 'string',
    )
  } catch {
    // Private browsing, a quota error or hand-edited JSON. A missing recent
    // list is not worth breaking navigation over.
    return []
  }
}

function write(entries: RecentRoute[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // Ignore
  }
}

export function useRecentRoutes() {
  const [recents, setRecents] = useState<RecentRoute[]>(read)

  const record = useCallback((entry: Omit<RecentRoute, 'visitedAt'>) => {
    setRecents((previous) => {
      // Re-visiting moves an entry to the top rather than duplicating it.
      const withoutThis = previous.filter((r) => r.path !== entry.path)
      const next = [{ ...entry, visitedAt: Date.now() }, ...withoutThis].slice(
        0,
        MAX_ENTRIES,
      )
      write(next)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    setRecents([])
    write([])
  }, [])

  return { recents, record, clear }
}

/**
 * Records the current route when the page supplies a human-readable name.
 *
 * Detail pages call this with the record's title. List and dashboard pages do
 * not call it at all — "Dashboard" in a recent list tells you nothing, and the
 * rail already links there.
 */
export function useRecordRecentRoute(
  label: string | undefined | null,
  context: string,
) {
  const location = useLocation()
  const { record } = useRecentRoutes()

  useEffect(() => {
    if (!label) return
    record({ path: location.pathname, label, context })
  }, [location.pathname, label, context, record])
}

/** Formats a visit time as a short relative string for the sidebar. */
export function useRelativeTime(visitedAt: number): string {
  return useMemo(() => {
    const seconds = Math.floor((Date.now() - visitedAt) / 1000)
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }, [visitedAt])
}
